import type { Account } from '@/api/types'
import { tenantAppHost, tenantAppUrl } from '@/lib/tenantAppUrl'
import { TenantMark } from './TenantMark'
import { RoleChip } from './RoleChip'

/**
 * One workspace in the picker — a full-width row, one per line.
 *
 * Renders as a native `<a>` so:
 *   - the browser gives us focus, Enter-activation and right-click
 *     "open in new tab" for free;
 *   - the click is a full navigation into another origin, which is what the
 *     handshake needs — the tenant app opens its own OAuth request against
 *     Passport, and the SPA must not try to own that route.
 */
export function TenantRow({ account }: { account: Account }) {
  const href = tenantAppUrl(account.subdomain)
  const host = tenantAppHost(account.subdomain)

  return (
    <a
      className="wsrow"
      href={href}
      aria-label={`Sign in to ${account.name} workspace, role: ${account.role}`}
    >
      <TenantMark
        logoDomain={account.logoDomain}
        initial={account.initial}
        name={account.name}
      />
      <span className="wsrow__body">
        <span className="wsrow__name">{account.name}</span>
        <span className="wsrow__meta">
          <span className="wsrow__host">{host}</span>
          <span className="wsrow__dot" aria-hidden="true">
            ·
          </span>
          <RoleChip role={account.role} />
        </span>
      </span>
      <svg
        className="wsrow__chev"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M6 3.5 10.5 8 6 12.5" />
      </svg>
    </a>
  )
}
